import type { CSSProperties, ElementType, ReactNode } from "react";
import { colors, typography } from "../design-system";

type Variant = keyof typeof typography;

type AppTextProps = {
  variant?: Variant;
  color?: string;
  /**
   * Élément rendu. Défaut `span`.
   *
   * Nouveauté web : `<Text>` n'avait aucune sémantique en natif. Ici, un titre
   * d'écran DOIT sortir en `<h1>`/`<h2>` et un paragraphe en `<p>` — c'est ce
   * que lisent les lecteurs d'écran et ce qu'indexe le moteur de recherche.
   * Le variant typographique ne présume PAS du niveau de titre : `variant="h2"`
   * est une taille, `as="h2"` est une structure. Les deux sont à choisir
   * séparément (un sous-titre peut être visuellement h2 sans être un h2).
   */
  as?: ElementType;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  /** Présent uniquement quand `as="label"`. */
  htmlFor?: string;
} & Omit<React.HTMLAttributes<HTMLElement>, "style" | "color">;

/**
 * Texte de l'app : applique un variant typographique, surchargeable par `style`.
 *
 * ## Ce qui a disparu au passage sur web
 * La version native embarquait `resolveLineHeightFix`, un rattrapage de
 * `lineHeight` : Android CLIPPE durement au lineHeight, donc un
 * `style={{ fontSize: 42 }}` posé sur un variant à `lineHeight: 22` rognait le
 * glyphe en haut et en bas — bug qui tronquait silencieusement les emoji de 19
 * écrans.
 *
 * Le navigateur ne clippe pas : un glyphe plus haut que sa boîte de ligne
 * déborde, il n'est jamais coupé. Le rattrapage n'a donc plus d'objet et a été
 * retiré plutôt que porté. Attention en revanche à l'effet de bord inverse : là
 * où Android coupait, le web fera CHEVAUCHER les lignes. Si un écran du Lot 6
 * montre des lignes qui se marchent dessus, la correction est de poser un
 * `lineHeight` explicite — surtout pas de réintroduire l'ancien calcul.
 *
 * Pour un emoji, préférer `<Emoji size={…}>` : un emoji n'a ni graisse ni
 * famille (Typold n'en contient pas — le système substitue sa police) et n'a
 * donc rien à faire dans un variant typographique.
 */
export function AppText({
  variant = "body",
  color = colors.black,
  as: Component = "span",
  className,
  style,
  children,
  ...props
}: AppTextProps) {
  return (
    <Component
      {...props}
      className={className}
      // L'ordre importe : le variant pose la base, `color` la surcharge, puis
      // `style` a le dernier mot — identique au comportement du tableau de
      // styles natif `[base, { color }, style]`.
      style={{ ...typography[variant], color, ...style }}
    >
      {children}
    </Component>
  );
}
